import DOMPurify from 'dompurify'

const dollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
})

/**
 * Format a number to a string with 2 decimal places: 2283 -> $2,283.00
 * @param {number} value
 * @returns {string}
 */
export const dollarFormat = value => dollarFormatter.format(value)

const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Formats a string representing a numeric value into a currency format.
 * It removes any non-numeric characters (except for the decimal point),
 * ensures the value is non-negative, converts it to a number, and then formats it
 * as a currency string with two decimal places and comma separators for thousands.
 * If the input string is empty, it returns an empty string.
 * If the input does not represent a valid number, or is negative, it returns "$0.00" as a placeholder.
 *
 * @param {string} value - The string to format as currency.
 * @returns {string} An empty string if the input is empty, "$0.00" if the input is not a valid number or negative,
 * or the formatted currency string otherwise.
 */
export const formatCurrency = value => {
  if (value === '') {
    return ''
  }

  const numberValue = Number(value.replace(/[^0-9.]+/g, ''))

  if (isNaN(numberValue) || numberValue < 0) {
    return '$0.00'
  }
  const formatted = numberValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return formatted.replace(/\.00$/, '')
}

/**
 * Format a number to a string with 2 decimal places: 2283 -> 2,283.00
 * @param {number} value
 * @returns {string}
 */
export const numberFormat = value => numberFormatter.format(value)

export const parseCurrency = value => {
  if (!value) return 0
  const numericString = value.toString().replace(/[^\d.-]/g, '')
  return parseFloat(numericString) || 0
}

const s = ['th', 'st', 'nd', 'rd']
export function getOrdinalNumberSuffix(n) {
  const v = n % 100
  return s[(v - 20) % 10] || s[v] || s[0]
}
export default function toOrdinalNumber(n) {
  return `${n}${getOrdinalNumberSuffix(n)}`
}

export const guidGenerator = () => btoa(Math.random().toString()).substring(0, 12)

export const rgbToHex = rgb => {
  const hex = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
  if (!hex) {
    return rgb
  }

  return `#${((1 << 24) | (parseInt(hex[1]) << 16) | (parseInt(hex[2]) << 8) | parseInt(hex[3])).toString(16).slice(1)}`.toLocaleUpperCase()
}

/**
 * Converts a given string to camelCase with the first letter in lowercase.
 * @param {string} str - The string to be converted.
 * @returns {string} - The string camel cased.
 */
export const toCamelCase = str => {
  return str
    .replace(/-/g, ' ')
    .split(' ')
    .map((word, index) => {
      if (index === 0) return word.toLowerCase()
      return word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase()
    })
    .join('')
}
/**
 * Converts a given string to slug with the words split by the - .
 * @param {string} str - The string to be converted.
 * @returns {string} - The string sluged.
 */
export const toSlug = str => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}
/**
 * Gets the initials of a string containing two names.
 * @param {string} names - The string containing two names.
 * @returns {string} - The initials of the names.
 */
export const getInitialsFromString = names => {
  const words = names.split(' ').filter(Boolean)
  let initials
  if (words.length === 1) {
    initials = names.substring(0, 2).toUpperCase()
  } else {
    initials = words.map(word => word[0].toUpperCase()).join('')
  }
  return initials
}

/**
 * Set the first letter uppercase
 * @param {string} string - The string
 * @returns {string} - The string with the first char uppercase.
 */
export const capitalizeFirstChar = string => {
  return string?.charAt(0).toUpperCase() + string.slice(1)
}
/**
 * Strip all HTML tags and sanitize the string.
 * @param {string} str - The string to sanitize and remove HTML tags.
 * @returns {string} - The plain text string with all HTML tags removed.
 */
export const stripHtmlTags = str => {
  const sanitizedString = DOMPurify.sanitize(str, { ALLOWED_TAGS: [] })
  return sanitizedString
}

/**
 * Checks if a string represents a negative number using parentheses, like (15) or (12.5).
 * @param {string} value - The string to check.
 * @returns {boolean} - True if the value is a negative number in parentheses.
 */
export const isParenthesisNegativeNumber = value => {
  if (typeof value !== 'string') return false
  return /^\(\d+(\.\d+)?\)$/.test(value)
}
